import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PostCard from "./PostCard";
import type { AnimePost, CategoryItem } from "@/mocks/anime";

const catColor: Record<string, string> = {
  recommend: "bg-primary-500 text-background-50",
  avoid: "bg-secondary-500 text-background-50",
  order: "bg-accent-500 text-foreground-950",
  review: "bg-primary-500 text-background-50",
};

interface CategoryLayoutProps {
  category: CategoryItem;
  posts: AnimePost[];
  description: string;
}

export default function CategoryLayout({ category, posts, description }: CategoryLayoutProps) {
  const [params] = useSearchParams();
  const [activeTag, setActiveTag] = useState("全部");
  const [query, setQuery] = useState("");

  const allTags = useMemo(() => {
    const set = new Set<string>();
    posts.forEach((p) => p.tags.forEach((t) => set.add(t)));
    return ["全部", ...Array.from(set)];
  }, [posts]);

  const filtered = useMemo(() => {
    const urlKeyword = params.get("q")?.trim() ?? "";
    const k = (query || urlKeyword).toLowerCase();
    return posts.filter((p) => {
      const tagOk = activeTag === "全部" || p.tags.includes(activeTag);
      const kwOk =
        !k ||
        p.title.toLowerCase().includes(k) ||
        p.excerpt.toLowerCase().includes(k) ||
        p.tags.some((t) => t.toLowerCase().includes(k));
      return tagOk && kwOk;
    });
  }, [posts, activeTag, query, params]);

  return (
    <div className="bg-background-50">
      {/* 分类横幅 */}
      <section className={`relative overflow-hidden ${catColor[category.id]} pt-24 md:pt-28`}>
        <div className="container-x relative z-10 py-12 md:py-16">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-2 text-xs text-background-50/80">
            <Link to="/" className="transition-colors hover:text-background-50">
              <i className="ri-home-4-line mr-1"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50/95">{category.name}</span>
          </nav>
          <h1 className="font-heading text-3xl text-background-50 md:text-4xl">{category.title}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-background-50/85 md:text-base">{description}</p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-2 text-sm text-background-50 backdrop-blur">
            <i className="ri-archive-line"></i>
            共 {posts.length} 篇内容
          </div>
        </div>
      </section>

      {/* 内容区 */}
      <section className="container-x py-10 md:py-12">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 ${
                  activeTag === tag
                    ? "bg-foreground-900 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 md:w-64">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="在当前栏目内搜索"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-500"
            />
          </label>
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p) => (
              <PostCard key={p.id} post={p} />
            ))}
          </div>
        ) : (
          <div className="card flex flex-col items-center justify-center py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-inbox-line"></i>
            </span>
            <h3 className="mt-4 font-bold text-foreground-800">没有找到相关内容</h3>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或标签试试看吧。</p>
            <button
              type="button"
              onClick={() => {
                setActiveTag("全部");
                setQuery("");
              }}
              className="mt-5 btn-outline"
            >
              重置筛选
            </button>
          </div>
        )}
      </section>

      {/* 关联栏目 */}
      <section className="bg-background-100">
        <div className="container-x py-10 md:py-12">
          <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">看看其他栏目</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {[
              { name: "求番推荐", desc: "不知道该看什么，来这里问", to: "/recommend" },
              { name: "避雷指南", desc: "烂番不踩坑，时间不白花", to: "/avoid" },
              { name: "补番顺序", desc: "按顺序补完老番一站搞定", to: "/order" },
              { name: "漫评讨论", desc: "私藏安利与深度漫评", to: "/review" },
            ]
              .filter((c) => c.to !== `/${category.id}`)
              .slice(0, 3)
              .map((c) => (
                <Link key={c.to} to={c.to} className="card group p-5 transition-colors duration-200 hover:border-primary-300">
                  <h3 className="flex items-center justify-between font-bold text-foreground-900">
                    {c.name}
                    <i className="ri-arrow-right-line text-primary-600 transition-transform duration-200 group-hover:translate-x-1"></i>
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600">{c.desc}</p>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}